import { Component, Inject, ViewChild } from '@angular/core';
import { MapComponent } from '../map/map.component';
import { RouteLocation, RouteMapDialogData } from '@shared/models/route-map-dialog-data.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LatLng } from '@shared/models/enums/lat-lng.model';

/** Shared by the stored-polyline draw and the Directions fallback so both render identically. */
const ROUTE_DIALOG_RENDERER_OPTIONS: google.maps.DirectionsRendererOptions = {
  suppressMarkers: true,
  draggable: false,
  polylineOptions: {
    strokeColor: '#6366F1',
    strokeWeight: 4,
    strokeOpacity: 0.8
  }
};

@Component({
  selector: 'hmt-route-map-dialog-component',
  standalone: false,
  templateUrl: './route-map-dialog-component.component.html',
  styleUrl: './route-map-dialog-component.component.scss'
})
export class RouteMapDialogComponent {
  @ViewChild('mapComponent', { static: false }) mapComponent!: MapComponent;

  locations: RouteLocation[] = [];
  /**
   * One row per actual stop, for the floating side panel - not the same thing as `locations`,
   * which is one entry per rendered MARKER. A marker can cover more than one stop (a bucket that
   * merged in a geofence-contained location, or the same location revisited back-to-back - see
   * RouteLocation.nameGroups), so reading the panel straight off `locations` silently dropped
   * every stop but the first at a merged/revisited marker. Expanding nameGroups here keeps this
   * list, and its count, in sync with the same total the Location Count badge already shows.
   */
  stopList: Array<{ name: string; type: string }> = [];
  totalDistance: string = '';
  totalTime: string = '';
  encodedPaths: string[] = [];
  private infoWindows: google.maps.InfoWindow[] = [];

  constructor(
    public dialogRef: MatDialogRef<RouteMapDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RouteMapDialogData
  ) {
    this.locations = data.locations || [];
    this.stopList = this.buildStopList(this.locations);
    this.totalDistance = data.totalDistance || '0 km';
    this.totalTime = data.totalTime || '0M';
    this.encodedPaths = (data.encodedPaths || []).filter(path => !!path);
  }

  private buildStopList(locations: RouteLocation[]): Array<{ name: string; type: string }> {
    return locations.flatMap(location => {
      if (!location.nameGroups?.length) {
        return [{ name: location.name, type: location.type }];
      }
      return location.nameGroups
        .flatMap(group => group.indices.map(index => ({ index, name: group.name })))
        .sort((a, b) => a.index - b.index)
        .map(({ name }) => ({ name, type: location.type }));
    });
  }

  ngOnInit(): void {
    // Component initialization
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeMap();
    }, 500);
  }

  ngOnDestroy(): void {
    this.closeAllInfoWindows();
  }

  private initializeMap(): void {
    if (!this.mapComponent?.map || this.locations.length === 0) {
      return;
    }
    
    this.addMarkersToMap();
    if (this.locations.length >= 2) {
      this.drawRoute();
    }
  }

  private addMarkersToMap(): void {
    const markers = this.generateMarkers();

    if (markers.length === 0) {
      return;
    }

    markers.forEach((markerOption, index) => {
      const marker = this.mapComponent.addMarker(markerOption, false);

      if (!marker) {
        return;
      }

      // Use consistent purple color for all locations
      const location = this.locations[index];
      const iconColor = '#6F68B9'; // Purple for all locations

      // A stop can cover more than one locationId - two merged via a shared geofence, or the same
      // one revisited back-to-back (see RouteLocation.nameGroups) - so render one row per group
      // instead of a single name, same as job-summary/container-route-map's combined marker label.
      const infoWindowContent = location.nameGroups?.length
        ? this.buildGroupedInfoWindowContent(location.nameGroups, location.type)
        : `
      <div class="custom-info-window">
        <div class="double-circle">
          <div class="inner-circle">${index + 1}</div>
        </div>

        <div style="text-align: center;">
          <div style="font-size: 10px; color: #666; padding-left: 4px;">${location.type}</div>
          <div style="font-size: 12px; font-weight: 700; padding-left: 4px;">${location.name}</div>
        </div>
      </div>
    `;


      const infoWindow = new google.maps.InfoWindow({
        content: infoWindowContent,
        pixelOffset: new google.maps.Size(0, -12),
        disableAutoPan: false
      });

      this.infoWindows.push(infoWindow);

      // Open info window automatically
      infoWindow.open(this.mapComponent.map, marker);

      // Add click listener to close other info windows when one is clicked
      marker.addListener('click', () => {
        this.closeAllInfoWindows();
        infoWindow.open(this.mapComponent.map, marker);
      });
    });

    if (markers.length > 0) {
      this.mapComponent.setMarkerBounds();
    }
    
    // Hide close button from info windows
    this.hideInfoWindowCloseButtons();
  }

  /** Same layout as job-summary's grouped marker info window: one row per distinct locationId. */
  private buildGroupedInfoWindowContent(nameGroups: Array<{ name: string; indices: number[] }>, type: string): string {
    const rows = nameGroups.map(group => `
      <div style="display: flex; align-items: center; gap: 6px;">
        <div style="
          width: 18px; height: 18px; border-radius: 50%; background: #6F68B9;
          border: 1.5px solid white; display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 700; color: white; flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        ">${group.indices.join(',')}</div>
        <div style="font-size: 12px; font-weight: 600; color: #1a1a1a; line-height: 1.2;">${group.name}</div>
      </div>
    `).join('');

    return `
    <div style="display: flex; flex-direction: column; gap: 4px; padding: 6px 10px;">
      ${rows}
      <div style="font-size: 10px; color: #666666; line-height: 1.2; margin-left: 24px;">${type}</div>
    </div>
  `;
  }

  private closeAllInfoWindows(): void {
    this.infoWindows.forEach(infoWindow => {
      infoWindow.close();
    });
  }

  private hideInfoWindowCloseButtons(): void {
    const styleId = 'gm-info-window-fix';
  
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        /* Remove default padding */
        .gm-style .gm-style-iw,
        .gm-style .gm-style-iw-c,
        .gm-style .gm-style-iw-d {
          padding: 0 !important;
          margin: 0 !important;
        }
  
        /* Center content */
        .gm-style .gm-style-iw-c {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
  
        /* Hide close button only */
        .gm-style .gm-ui-hover-effect {
          display: none !important;
        }
  
        /* Remove down arrow tip completely */
        .gm-style .gm-style-iw-t::before,
        .gm-style .gm-style-iw-t::after {
          display: none !important;
        }
        .gm-style .gm-style-iw-tc {
          display: none !important;
        }
        .gm-style .gm-style-iw-tc::before,
        .gm-style .gm-style-iw-tc::after {
          display: none !important;
        }
        .gm-style .gm-style-iw-t {
          background: transparent !important;
        }
  
        /* Your content wrapper */
        .custom-info-window {
          display: flex;
          align-items: center;
          justify-content: center;
          padding-left: 12px;
          padding-bottom: 6px;
        }
        /* Double circle number marker */
        .double-circle {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(111, 104, 185, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .inner-circle {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #6F68B9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          font-weight: 700;
          color: #ffffff;
          line-height: 1;
        }


      `;
      document.head.appendChild(style);
    }
  }
  

  private generateMarkers(): google.maps.MarkerOptions[] {
    return this.locations
      .map((location, index): google.maps.MarkerOptions | null => {
        const geoLocation: LatLng = {
          lat: location.coordinates.lat,
          lng: location.coordinates.lng
        };
        
        if (!geoLocation || geoLocation.lat === 0 || geoLocation.lng === 0) {
          return null;
        }

        // Use consistent purple color for all markers
        const iconColor = '#6F68B9'; // Purple for all locations

        const svgMarker = `
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg">
            <circle cx="9" cy="9" r="7.5" fill="${iconColor}" stroke="white" stroke-width="1.5"/>
          </svg>
        `;

        const icon = {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker),
          scaledSize: new google.maps.Size(18, 18),
          anchor: new google.maps.Point(9, 9),
        };

        return {
          position: geoLocation,
          icon,
          title: location.name
        };
      })
      .filter((marker): marker is google.maps.MarkerOptions => marker !== null);
  }

  private drawRoute(): void {
    if (this.locations.length < 2) {
      return;
    }

    // The caller handed us the route ms-jobfile already stored - draw it and skip Directions.
    if (this.encodedPaths.length > 0) {
      this.encodedPaths.forEach((encodedPath, index) =>
        this.mapComponent.drawStoredPath(index, encodedPath, ROUTE_DIALOG_RENDERER_OPTIONS));
      return;
    }

    const waypoints: google.maps.DirectionsWaypoint[] = [];
    for (let i = 1; i < this.locations.length - 1; i++) {
      const location = this.locations[i];
      waypoints.push({
        location: new google.maps.LatLng(location.coordinates.lat, location.coordinates.lng),
        stopover: true
      });
    }

    const origin: LatLng = {
      lat: this.locations[0].coordinates.lat,
      lng: this.locations[0].coordinates.lng
    };
    
    const destination: LatLng = {
      lat: this.locations[this.locations.length - 1].coordinates.lat,
      lng: this.locations[this.locations.length - 1].coordinates.lng
    };

    if (!origin || !destination || !this.mapComponent?.map) {
      return;
    }

    this.mapComponent.drawPath(
      origin,
      destination,
      waypoints,
      false,
      0,
      ROUTE_DIALOG_RENDERER_OPTIONS,
      false
    ).subscribe();
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
